import { LocationStrategy } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  HostBinding,
  HostListener,
  Injectable,
  Input,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Router,
} from '@angular/router';

type Params = { [key: string]: any };

type QueryParamsHandling = 'merge' | 'preserve' | '';

@Injectable()
export class RivetLinkHelper {
  constructor(private readonly activatedRoute: ActivatedRoute) {}

  params(
    params: Params,
    rivetLink: any[] | null,
    queryParamsHandling: QueryParamsHandling | null,
    replaceUrl: boolean | null,
  ): NavigationExtras {
    const qph =
      queryParamsHandling === null
        ? rivetLink!.length === 0
          ? 'merge'
          : ''
        : queryParamsHandling;
    const ru =
      replaceUrl === null ? (rivetLink === null ? true : null) : replaceUrl;

    return {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: qph,
      replaceUrl: ru!,
    };
  }

  route(rivetLink: any[] | string): any[] {
    return Array.isArray(rivetLink) ? rivetLink : rivetLink ? [rivetLink] : [];
  }
}

@Directive({
  providers: [RivetLinkHelper],
  selector: ':not(a):not(area)[rivetLink]',
})
export class RivetLinkDirective {
  @Input()
  rivetLink: any[] | string;

  @Input()
  queryParams: { [key: string]: any };

  @Input()
  replaceUrl: boolean | null = null;

  @Input()
  queryParamsHandling: QueryParamsHandling | null = null;

  @Input()
  activeClass: boolean = false;

  @Input()
  locked: boolean;

  @HostBinding('class.active')
  active: boolean;

  @Input()
  activeExact: boolean = false;

  @Input()
  target: string = '_self';

  @Input()
  url: string = '';

  subscription;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly rivetLinkHelper: RivetLinkHelper,
    private readonly router: Router,
  ) {
    this.subscription = router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.updateActive();
      }
    });
  }

  updateActive() {
    if (this.activeClass) {
      const routes = this.rivetLinkHelper.route(this.rivetLink);
      const params = this.rivetLinkHelper.params(
        this.queryParams,
        routes,
        this.queryParamsHandling,
        this.replaceUrl,
      );
      const urlTree = this.router.createUrlTree(routes, params);
      this.active = this.router.isActive(urlTree, this.activeExact);
      this.changeDetectorRef.markForCheck();
    } else {
      this.active = false;
    }
  }

  @HostListener('click')
  click() {
    if (!this.locked) {
      const routes = this.rivetLinkHelper.route(this.rivetLink);
      if (this.target == '_blank' && this.url != '') {
        let newUrl = this.url;
        for (const key in this.queryParams) {
          if (newUrl.indexOf(key) !== -1) {
            const regex = new RegExp(key + '=[\\w-~_%]*');
            newUrl = newUrl.replace(
              regex,
              this.queryParams[key] === null
                ? ''
                : key + '=' + this.queryParams[key],
            );
          } else if (this.queryParams[key]) {
            if (newUrl.indexOf('?') === -1) {
              newUrl = newUrl.concat('?');
            }
            newUrl = newUrl.concat('&', key, '=', this.queryParams[key]);
          }
        }
        window.open(newUrl, '_blank');
      } else {
        this.router.navigate(
          routes,
          this.rivetLinkHelper.params(
            this.queryParams,
            routes,
            this.queryParamsHandling,
            this.replaceUrl,
          ),
        );
      }
    }
  }
}

@Directive({
  providers: [RivetLinkHelper],
  selector: 'a[rivetLink], area[rivetLink]',
})
export class RivetLinkDirectiveWithHref {
  @HostBinding('attr.target') @Input() target!: string;

  @Input()
  rivetLink: any[] | string = [];

  @Input()
  queryParams: { [key: string]: any };

  @Input()
  replaceUrl: boolean | null = null;

  @Input()
  queryParamsHandling: QueryParamsHandling | null = null;

  @Input()
  activeClass: boolean = false;

  @Input()
  activeExact: boolean = false;

  @Input()
  locked: boolean;

  subscription;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly locationStrategy: LocationStrategy,
    private readonly rivetLinkHelper: RivetLinkHelper,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.subscription = router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.updateActive();
      }
    });
  }

  ngAfterContentInit() {
    this.updateActive();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges() {
    this.updateActive();
  }

  @HostBinding('class.active')
  active: boolean;

  updateActive() {
    if (this.activeClass) {
      const routes = this.rivetLinkHelper.route(this.rivetLink);
      const params = this.rivetLinkHelper.params(
        this.queryParams,
        routes,
        this.queryParamsHandling,
        this.replaceUrl,
      );
      const urlTree = this.router.createUrlTree(routes, params);
      this.active = this.router.isActive(urlTree, this.activeExact);
      this.changeDetectorRef.markForCheck();
    } else {
      this.active = false;
    }
  }

  @HostListener('click', [
    '$event.button',
    '$event.ctrlKey',
    '$event.metaKey',
    '$event.shiftKey',
  ])
  onClick(
    button: number,
    ctrlKey: boolean,
    metaKey: boolean,
    shiftKey: boolean,
  ): boolean | undefined {
    if (this.locked) {
      return;
    }

    if (
      button !== 0 ||
      ctrlKey ||
      metaKey ||
      shiftKey ||
      (typeof this.target === 'string' && this.target != '_self')
    ) {
      return;
    }

    const routes = this.rivetLinkHelper.route(this.rivetLink);
    const params = this.rivetLinkHelper.params(
      this.queryParams,
      routes,
      this.queryParamsHandling,
      this.replaceUrl,
    );
    this.router.navigate(routes, params);
    return false;
  }

  @HostBinding('attr.href')
  get href() {
    if (this.locked) {
      return null;
    }
    const routes = this.rivetLinkHelper.route(this.rivetLink);
    const params = this.rivetLinkHelper.params(
      this.queryParams,
      routes,
      this.queryParamsHandling,
      this.replaceUrl,
    );
    const urlTree = this.router.createUrlTree(routes, params);
    return this.locationStrategy.prepareExternalUrl(
      this.router.serializeUrl(urlTree),
    );
  }
}
